import moment from 'moment'
import { GraphData, GraphDatum } from './types/graphData'
import History, { HistoryItem } from '@/models/History'
import { GraphColor, XRange, XRangeType } from '@/lib/graph/types/initResult'
import { useHistoryStore } from '@/stores/historyStore'
import { useInitDataStore } from '@/stores/initDataStore'
import { useChartConfigStore } from '@/stores/chartConfigStore'
import { rangeToISO } from '@/lib/formatter'
import { WEATHER_FIELDS } from '@/lib/constants'

export class HistoryDataService {
  initDataStore!: ReturnType<typeof useInitDataStore>
  historyStore!: ReturnType<typeof useHistoryStore>
  chartConfigStore!: ReturnType<typeof useChartConfigStore>

  constructor() {
    this.initDataStore = useInitDataStore()
    this.historyStore = useHistoryStore()
    this.chartConfigStore = useChartConfigStore()
  }

  /**
   * ひとつの局の観測データを取得
   */
  async fetchOne(
    stationId: string,
    start: number,
    end: number,
    targets: string[],
    xRangeType: XRangeType
  ): Promise<GraphData> {
    const range = {
      startTime: moment(start).format(),
      endTime: moment(end).format(),
    } as Range
    const params = {
      stationId,
      range: {
        ...rangeToISO(range),
        type: xRangeType,
      },
      target: targets,
    }
    const data: History = await this.historyStore.fetch(params)
    if (!data?.items[0] || Object.keys(data.items[0]).length == 0) {
      return {
        data: [],
        labels: [],
      }
    }
    const result = {
      data: [] as GraphDatum[],
      labels: [],
    }
    const station = this.initDataStore.initData.stationList.find(station => station.stationId == stationId)

    for (let i = 0; i < targets.length; i++) {
      const key = targets[i]

      if (key === 'alertLevel' || key === 'time') continue

      result.data.push({
        label: key,
        data: data.items.map((item: HistoryItem) => {
          const x =
            xRangeType === XRange.MONTH || xRangeType == XRange.YEAR
              ? moment(item.time)
                  .subtract(1, 'second')
                  .startOf(xRangeType == XRange.MONTH ? 'day' : 'month')
                  .toDate()
                  .getTime()
              : moment(item.time).toDate().getTime()
          let y = Number(item[key as keyof HistoryItem])

          if (station?.ap && key === 'waterLevel') {
            y += station.ap
          }

          return { x, y }
        }),
        yAxisID: this.initDataStore.getUnit(key),
      })
    }

    return result
  }

  /**
   * 局毎の観測データをまとめて多局グラフ用のデータを成形
   * @param spots
   * @param targets
   * @returns
   */
  async fetch(
    minimapStart: number,
    minimapEnd: number,
    spots: string[],
    targets: string[],
    xRangeType: XRangeType,
    colors: string[]
  ) {
    this.chartConfigStore.startLoad()

    const data: GraphData[] = []
    let dataList: { id: string; data: GraphData }[]

    if (targets.length === 1) {
      dataList = await Promise.all(
        spots.map(async spot => {
          return this.fetchOne(spot, minimapStart, minimapEnd, targets, xRangeType).then(result => {
            return {
              id: spot,
              data: result,
            }
          })
        })
      )

      spots.forEach((s, i) => {
        const datum = dataList.find(d => d.id === s)

        if (datum) {
          data[i] = datum.data
        }
      })
    } else {
      const spot = spots[0]
      const response = await this.fetchOne(spot, minimapStart, minimapEnd, targets, xRangeType)

      targets.forEach((t, i) => {
        const d = response.data.find(d => d.label === t)

        if (d) {
          data[i] = { data: [d], labels: [] }
        }
      })
    }

    colors = colors || GraphColor
    const obj = [] as GraphDatum[]
    const target = targets[0]

    data.forEach((d, index) => {
      const found = targets.length === 1 ? d.data.find(d => d.label == target) : d.data[0]
      const label = targets.length === 1 ? spots[index] : targets[index]
      const type = (targets.length > 1 && targets[index] === 'rainRui' ? 'bar' : 'line') as 'line' | undefined
      const foundData = found?.data || []
      const res = {
        label,
        data: Array.from(foundData),
        borderColor: colors[index],
        backgroundColor: colors[index],
        borderWidth: 4,
        yAxisID: found?.yAxisID as string,
        pointRadius: 0,
        type,
        pointBorderColor: colors[index],
        pointBackgroundColor: '#ffffff',
        spanGaps: this.getSecondInterval(xRangeType),
        order: type === 'line' ? -1 : 1, // 棒グラフを裏側に描画
      }

      obj.push(res)
    })
    const labels = this.setLabels(moment(minimapStart), moment(minimapEnd), xRangeType)
    this.chartConfigStore.endLoad()

    return {
      data: obj,
      labels,
    }
  }

  /**
   * 使用されている単位(initのfield_listより)を基にグラフのタイプを指定
   * @param fieldName
   * @returns
   */
  setGraphType(fieldName: string) {
    const unit = this.initDataStore.initData.fieldList[fieldName].unit
    const types: { [key: string]: string[] } = {
      line: ['%', '℃', 'mm', 'm'],
    }
    return Object.keys(types).find(typeName => types[typeName]?.find(items => items.indexOf(unit) >= 0))
  }

  getStationNameFromStationId(id: string): string {
    const s = this.initDataStore.initData.stationList.find(station => station.stationId == id)
    return s ? s.name : id
  }

  getFieldNameFromFieldId(id: string): string {
    const f = this.initDataStore.initData.fieldList[id]
    const weatherName = WEATHER_FIELDS.find(f => f.value === id)

    return f?.name || weatherName?.label || id
  }

  getUnitFromFieldId(id: string): string {
    const unit = this.initDataStore.initData.fieldList[id]?.unit
    const weatherName = WEATHER_FIELDS.find(f => f.value === id)

    return unit || weatherName?.unit || id
  }

  getSearchableSpots(target: string) {
    const list = [...this.initDataStore.initData.stationList]
    const type = this.initDataStore.initData.fieldList[target].type
    const result = list.filter(station => station.fieldId.find(field => field.indexOf(type) >= 0) !== undefined)
    return result
  }

  getSecondInterval(xRange: XRangeType): number {
    switch (xRange) {
      case XRange.HOUR:
        return 1000 * 60 * 10
      case XRange.DAY:
        return 60 * 60 * 1000
      case XRange.MONTH:
        return 60 * 60 * 24 * 1000
      case XRange.YEAR:
      default:
        return 60 * 60 * 24 * 31 * 1000
    }
  }

  setLabels(start: moment.Moment, end: moment.Moment, xRangeType: XRangeType): number[] {
    let markerSpanUnit!: moment.unitOfTime.StartOf

    switch (xRangeType) {
      case XRange.HOUR:
        markerSpanUnit = 'minute'
        break
      case XRange.DAY:
        markerSpanUnit = 'hour'
        break
      case XRange.MONTH:
        markerSpanUnit = 'day'
        break
      case XRange.YEAR:
      default:
        markerSpanUnit = 'month'
        break
    }
    const labels: number[] = []
    const delta = markerSpanUnit === 'minute' ? 10 : 1
    let second = start.toDate().getTime()

    while (second <= end.toDate().getTime()) {
      labels.push(second)
      second = moment(second).add(delta, markerSpanUnit).toDate().getTime()
    }
    return labels
  }
}
